import { motion } from "framer-motion";

// Simple cn utility
const cn = (...classes) => classes.filter(Boolean).join(" ");

const FlowNode = ({
  id,
  label,
  sublabel,
  shape = "circle",
  isActive = false,
  intensity = 0,
  onClick,
  className,
}) => {
  const size = 24;

  const renderShape = () => {
    const baseStroke = isActive ? "#4a9eb5" : "#2a2f38";
    const baseFill = isActive ? "rgba(74,158,181,0.15)" : "rgba(37,42,53,0.9)";

    switch (shape) {
      case "circle":
        return (
          <svg width={size} height={size} viewBox="0 0 24 24">
            <circle
              cx="12"
              cy="12"
              r="10"
              fill={baseFill}
              stroke={baseStroke}
              strokeWidth="1.5"
            />
            {isActive && (
              <motion.circle
                cx="12"
                cy="12"
                r="10"
                fill="none"
                stroke="#4a9eb5"
                strokeWidth="1"
                strokeDasharray="3 2"
                animate={{ rotate: 360 }}
                transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
                style={{ transformOrigin: "center" }}
              />
            )}
          </svg>
        );

      case "hexagon":
        return (
          <svg width={size} height={size} viewBox="0 0 24 24">
            <polygon
              points="12,2 22,7 22,17 12,22 2,17 2,7"
              fill={baseFill}
              stroke={baseStroke}
              strokeWidth="1.5"
            />
            {isActive && (
              <motion.polygon
                points="12,4 20,8 20,16 12,20 4,16 4,8"
                fill="none"
                stroke="rgba(74,158,181,0.5)"
                strokeWidth="0.75"
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
                style={{ transformOrigin: "center" }}
              />
            )}
          </svg>
        );

      case "diamond":
        return (
          <svg width={size} height={size} viewBox="0 0 24 24">
            <polygon
              points="12,2 22,12 12,22 2,12"
              fill={baseFill}
              stroke={baseStroke}
              strokeWidth="1.5"
            />
            {isActive && (
              <motion.polygon
                points="12,5 19,12 12,19 5,12"
                fill="none"
                stroke="rgba(74,158,181,0.4)"
                strokeWidth="0.75"
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 1.5, repeat: Infinity }}
                style={{ transformOrigin: "center" }}
              />
            )}
          </svg>
        );

      case "ring":
        return (
          <svg width={size} height={size} viewBox="0 0 24 24">
            <circle
              cx="12"
              cy="12"
              r="9"
              fill="none"
              stroke={isActive ? "rgba(74,158,181,0.4)" : "rgba(58,63,74,0.6)"}
              strokeWidth="4"
            />
            <circle
              cx="12"
              cy="12"
              r="5"
              fill={baseFill}
              stroke={baseStroke}
              strokeWidth="1.5"
            />
            {isActive && (
              <motion.circle
                cx="12"
                cy="12"
                r="9"
                fill="none"
                stroke="#4a9eb5"
                strokeWidth="1"
                strokeDasharray="4 3"
                animate={{ rotate: -360 }}
                transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
                style={{ transformOrigin: "center" }}
              />
            )}
          </svg>
        );

      case "pill":
        return (
          <svg width={size + 12} height={size - 4} viewBox="0 0 36 20">
            <rect
              x="2"
              y="2"
              width="32"
              height="16"
              rx="8"
              fill={baseFill}
              stroke={baseStroke}
              strokeWidth="1.5"
            />
            {isActive && (
              <motion.rect
                x="5"
                y="5"
                width="26"
                height="10"
                rx="5"
                fill="none"
                stroke="rgba(74,158,181,0.4)"
                strokeWidth="0.75"
                animate={{ opacity: [0.3, 0.7, 0.3] }}
                transition={{ duration: 2, repeat: Infinity }}
              />
            )}
          </svg>
        );

      default:
        return (
          <svg width={size} height={size} viewBox="0 0 24 24">
            <circle
              cx="12"
              cy="12"
              r="10"
              fill={baseFill}
              stroke={baseStroke}
              strokeWidth="1.5"
            />
          </svg>
        );
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={{ scale: 1.15 }}
      whileTap={{ scale: 0.95 }}
      transition={{ duration: 0.3, type: "spring", stiffness: 300 }}
      onClick={onClick}
      className={cn("flow-node", className)}
      style={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        cursor: onClick ? "pointer" : "default",
      }}
    >
      {/* Floating Label Above */}
      <motion.div
        style={{
          position: "absolute",
          top: "-32px",
          left: "50%",
          transform: "translateX(-50%)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          whiteSpace: "nowrap",
        }}
        initial={{ opacity: 0, y: 5 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <span
          style={{
            fontSize: "10px",
            fontWeight: 500,
            letterSpacing: "0.05em",
            color: isActive ? "#4a9eb5" : "#a3a3a3",
            transition: "color 0.3s ease",
          }}
        >
          {label}
        </span>
        {sublabel && (
          <span
            style={{
              fontSize: "8px",
              marginTop: "2px",
              color: isActive ? "rgba(74,158,181,0.6)" : "#6a7080",
              transition: "color 0.3s ease",
            }}
          >
            {sublabel}
          </span>
        )}
      </motion.div>

      {/* Glow effect */}
      {isActive && (
        <motion.div
          style={{
            position: "absolute",
            width: size + 8,
            height: size + 8,
            marginLeft: -4,
            marginTop: -4,
            borderRadius: "50%",
            background: "rgba(74,158,181,0.3)",
            filter: "blur(8px)",
            zIndex: -1,
          }}
          animate={{ opacity: [0.4, 0.7, 0.4], scale: [0.9, 1.2, 0.9] }}
          transition={{ duration: 2, repeat: Infinity }}
        />
      )}

      {/* Node Shape */}
      <div style={{ position: "relative", zIndex: 10 }}>{renderShape()}</div>

      {/* Intensity indicator below */}
      {intensity > 0 && (
        <div
          style={{
            position: "absolute",
            bottom: "-12px",
            left: "50%",
            transform: "translateX(-50%)",
            display: "flex",
            gap: "2px",
          }}
        >
          {[...Array(5)].map((_, i) => (
            <motion.div
              key={i}
              style={{
                width: "4px",
                height: "4px",
                borderRadius: "50%",
                background: i < intensity ? "#4a9eb5" : "#1a1f28",
              }}
              animate={i < intensity ? { scale: [1, 1.4, 1] } : undefined}
              transition={{ duration: 0.6, delay: i * 0.08, repeat: Infinity }}
            />
          ))}
        </div>
      )}
    </motion.div>
  );
};

export default FlowNode;
